import React, { useCallback, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  InputGroup,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  Plus,
  RefreshCw,
  Edit2,
  Trash2,
  Play,
  Eye,
  Search,
} from "react-feather";
import { Link } from "react-router";
import { useGet, useDelete, usePost } from "../../api";
import {
  IBasketTest,
  IBasketTestsResponse,
  IBasketTestRunResult,
} from "../../interfaces";
import { BasketFormModal } from "./basket-form-modal";

const PAGE_SIZE = 20;

const statusBadge = (t: IBasketTest) => {
  if (!t.lastRunStatus) return <Badge bg="secondary">Never run</Badge>;
  return (
    <Badge bg={t.lastRunStatus === "success" ? "success" : "danger"}>
      {t.lastRunStatus}
    </Badge>
  );
};

export const BasketTests: React.FC = () => {
  const [page, setPage] = useState(0);
  const [qInput, setQInput] = useState("");
  const [domainInput, setDomainInput] = useState("");
  const [countryInput, setCountryInput] = useState("");
  const [qFilter, setQFilter] = useState("");
  const [domainFilter, setDomainFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<IBasketTest | null>(null);
  const [running, setRunning] = useState<string | null>(null);

  const handleSearch = () => {
    setQFilter(qInput);
    setDomainFilter(domainInput);
    setCountryFilter(countryInput.toUpperCase());
    setPage(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const offset = page * PAGE_SIZE;
  const params = new URLSearchParams();
  params.set("offset", String(offset));
  params.set("limit", String(PAGE_SIZE));
  if (qFilter) params.set("q", qFilter);
  if (domainFilter) params.set("domain", domainFilter);
  if (countryFilter) params.set("countryCode", countryFilter.toUpperCase());

  const { data, loading, error, refetch } = useGet<IBasketTestsResponse>(
    `/basket-testing?${params}`
  );

  const deleteHook = useDelete<void>();
  const runHook = usePost<IBasketTestRunResult, Record<string, never>>();

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this basket test and all its run history?"))
      return;
    await deleteHook.execute(`/basket-testing/${id}`);
    refetch();
  };

  const handleRun = useCallback(
    async (id: string) => {
      setRunning(id);
      try {
        await runHook.execute(`/basket-testing/${id}/run`, {});
      } finally {
        setRunning(null);
        refetch();
      }
    },
    [runHook, refetch]
  );

  const handleSaved = () => {
    setShowForm(false);
    setEditing(null);
    refetch();
  };

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0">Basket Tests</h5>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={refetch}
            disabled={loading}
          >
            <RefreshCw size={14} />
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
          >
            <Plus size={14} className="me-1" />
            New basket
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Row className="mb-3 g-2 align-items-end">
        <Col md={4}>
          <Form.Control
            size="sm"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search by name, domain or description…"
          />
        </Col>
        <Col md={3}>
          <InputGroup size="sm">
            <InputGroup.Text>Domain</InputGroup.Text>
            <Form.Control
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Filter by domain"
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <InputGroup size="sm">
            <InputGroup.Text>Country</InputGroup.Text>
            <Form.Control
              value={countryInput}
              onChange={(e) => setCountryInput(e.target.value.toUpperCase())}
              onKeyDown={handleKeyDown}
              placeholder="US"
              maxLength={2}
            />
          </InputGroup>
        </Col>
        <Col xs="auto">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={handleSearch}
            disabled={loading}
          >
            <Search size={14} className="me-1" />
            Search
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error.message}</Alert>}

      {loading ? (
        <div className="text-center py-4">
          <Spinner />
        </div>
      ) : (
        <>
          <Table hover responsive size="sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Domain</th>
                <th>Country</th>
                <th>Items</th>
                <th>Last run</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data?.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-3">
                    No basket tests yet
                  </td>
                </tr>
              )}
              {data?.items.map((t) => (
                <tr key={t._id}>
                  <td>
                    <Link to={`/affiliate-products/basket-tests/${t._id}`}>
                      {t.name}
                    </Link>
                  </td>
                  <td className="text-muted small">{t.domain}</td>
                  <td>{t.countryCode}</td>
                  <td>{t.items.length}</td>
                  <td className="text-muted small">
                    {t.lastRunAt ? new Date(t.lastRunAt).toLocaleString() : "—"}
                  </td>
                  <td>{statusBadge(t)}</td>
                  <td className="text-end text-nowrap">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 me-2"
                      as={Link as any}
                      to={`/affiliate-products/basket-tests/${t._id}`}
                      title="View results"
                    >
                      <Eye size={14} />
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 me-2"
                      onClick={() => {
                        setEditing(t);
                        setShowForm(true);
                      }}
                      title="Edit"
                    >
                      <Edit2 size={14} />
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 me-2 text-success"
                      onClick={() => handleRun(t._id)}
                      disabled={running === t._id}
                      title="Run"
                    >
                      {running === t._id ? (
                        <Spinner size="sm" />
                      ) : (
                        <Play size={14} />
                      )}
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-danger"
                      onClick={() => handleDelete(t._id)}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {data && (
            <div className="d-flex align-items-center justify-content-between mt-2">
              <small className="text-muted">
                {data.total === 0
                  ? "No results"
                  : `Showing ${data.offset + 1}–${Math.min(data.offset + PAGE_SIZE, data.total)} of ${data.total}`}
              </small>
              {totalPages > 1 && (
                <Pagination size="sm" className="mb-0">
                  <Pagination.Prev
                    disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                  />
                  {Array.from({ length: totalPages }, (_, i) => (
                    <Pagination.Item
                      key={i}
                      active={i === page}
                      onClick={() => setPage(i)}
                    >
                      {i + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  />
                </Pagination>
              )}
            </div>
          )}
        </>
      )}

      <BasketFormModal
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setEditing(null);
        }}
        existing={editing}
        onSaved={handleSaved}
      />
    </>
  );
};
